import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import format from 'date-fns/format';
import React from 'react';
import { useParams } from 'react-router-dom';
import { RouteContainer } from '../../components/route-container';
import { AuthContext } from '../../contexts/auth-context';
import { EventRegistrationData } from '../../types';
import { EventNotFound } from '../event/event-not-found';
import { RegisterAgent } from './register-agent';
import { RegisterGuest } from './register-guest';

type RouteParams = { eventId?: string };
export function Purchase() {
  // Context
  const { state } = React.useContext(AuthContext);
  // Hooks
  const params: RouteParams = useParams();

  // Query - Regristation Info for Event
  const path = '/events/registration/info';
  const query = useQuery({
    queryKey: [path, { EventCode: params.eventId }],
    queryFn: async () => {
      const response: {
        data: EventRegistrationData;
      } = await API.post('EventsAPI', '/events/registration/info', {
        body: { EventCode: params.eventId },
      });

      const DateBegin = response.data.DateBegin
        ? response.data.DateBegin.substring(0, 19)
        : null;
      const DateEnd = response.data.DateEnd
        ? response.data.DateEnd.substring(0, 19)
        : null;

      return { ...response.data, DateBegin, DateEnd };
    },
  });

  let content = <div />;
  if (query.data) {
    let registerForm = <div />;
    if (query.data.TicketTypes.length === 0) {
      registerForm = (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <div
            style={{
              height: 80,
              width: 80,
              borderRadius: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '40px auto',
              backgroundColor: '#f1f1f1',
            }}
          >
            <ConfirmationNumberIcon fontSize="large" color="primary" />
          </div>

          <Typography variant="h5" style={{ margin: '40px auto' }}>
            No tickets have been created for this event yet.
          </Typography>

          <Typography style={{ margin: '40px auto' }}>
            Please check back soon.
          </Typography>
        </div>
      );
    } else if (state.user !== undefined) {
      registerForm = <RegisterAgent data={query.data} user={state.user} />;
    } else {
      registerForm = <RegisterGuest data={query.data} />;
    }

    content = (
      <Container maxWidth="md">
        <Box
          sx={{
            marginTop: 2,
            borderRadius: '10px 10px 0 0',
            backgroundColor: '#777',
            backgroundImage: query.data.BannerImagePath
              ? `url(${query.data.BannerImagePath})`
              : 'none',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(to right, #12121299, #12121200)`,
              minHeight: 120,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Box sx={{ pl: 2 }}>
              <Box
                sx={{
                  color: '#fff',
                  textShadow: '2px 2px rgba(0,0,0,0.6)',
                  fontSize: 24,
                  fontWeight: 'bold',
                  fontFamily: 'Oswald',
                  textTransform: 'uppercase',
                  letterSpacing: 1.1,
                }}
              >
                {query.data.Title}
              </Box>

              {query.data.DateBegin && query.data.DateEnd ? (
                <Box sx={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>
                  {format(new Date(query.data.DateBegin), 'MMM d')} -{' '}
                  {format(new Date(query.data.DateEnd), 'PP')}
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>

        {registerForm}
      </Container>
    );
  } else {
    // Could not find event
    content = <EventNotFound />;
  }

  let routeTitle = '';
  if (query.isLoading) {
    routeTitle = '';
  } else if (query.data) {
    routeTitle = 'Tickets for ' + query.data.Title || '';
  }

  return (
    <RouteContainer
      routeTitle={routeTitle}
      noanimation
      loading={query.isLoading}
      hasError={query.isError}
      hideFooter
      paddingBottom={160}
    >
      {content}
    </RouteContainer>
  );
}
